import { useCallback, type FC } from "react";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/modal/Modal";
import { Button } from "@/components/flexkit/Button";
import { TrackLocations } from "@/hooks/use-segment";
import type { ArchivedEnvironment } from "@/utils/environment";
import { useDeleteEnvironment } from "@/queries/environment-queries";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";

type ArchivedEnvironmentDeleteModalProps = {
    onClose: () => void;
    archivedEnvironments: ArchivedEnvironment[];
};

export const ArchivedEnvironmentDeleteModal: FC<ArchivedEnvironmentDeleteModalProps> = ({
    onClose,
    archivedEnvironments,
}) => {
    const deleteEnvironment = useDeleteEnvironment();
    const { toast } = useToast();

    const onOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                onClose();
            }
        },
        [onClose],
    );

    const handleDelete = useCallback(async () => {
        const errors: { id: string; name?: string; error: string }[] = [];

        for (const env of archivedEnvironments) {
            try {
                await deleteEnvironment.mutateAsync({
                    environmentId: env.environment.id,
                    force: true,
                });
            } catch (err) {
                errors.push({
                    id: env.environment.id,
                    name: env.environment.metadata?.name,
                    error: formatError(err),
                });
            }
        }

        if (errors.length === 0) {
            toast({
                title:
                    archivedEnvironments.length === 1
                        ? "Environment deleted successfully"
                        : "All environments deleted successfully",
            });
        } else {
            const errorDetails = errors.map((err) => `${err.name || err.id}: ${err.error}`).join("\n");
            toast({
                title: `${errors.length} out of ${archivedEnvironments.length} environments failed to delete.`,
                description: <p className="text-muted-foreground mt-2 whitespace-pre-wrap text-sm">{errorDetails}</p>,
            });
        }
        onClose();
    }, [archivedEnvironments, deleteEnvironment, onClose, toast]);

    return (
        <Dialog open onOpenChange={onOpenChange}>
            <DialogContent
                data-track-location={TrackLocations.DeleteArchivedEnvironmentModal}
                className="max-w-[480px]"
            >
                <DialogHeader>
                    <DialogTitle>
                        {archivedEnvironments.length === 1
                            ? "Delete archived environment?"
                            : "Delete all archived environments?"}
                    </DialogTitle>
                </DialogHeader>
                <DialogBody>
                    <DialogDescription>
                        {archivedEnvironments.length === 1
                            ? "Deleting this environment will permanently stop all running services and remove all content, including code, configurations, and data. This action cannot be undone."
                            : `Deleting these ${archivedEnvironments.length} environments will permanently stop all running services and remove all content, including code, configurations, and data. This action cannot be undone.`}
                    </DialogDescription>
                </DialogBody>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="secondary" disabled={deleteEnvironment.isPending}>
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructive"
                        onClick={handleDelete}
                        disabled={deleteEnvironment.isPending}
                        loading={deleteEnvironment.isPending}
                    >
                        {deleteEnvironment.isPending ? "Deleting..." : "Yes, Delete"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
